/**
 * Persist fill dialog tab index per puzzle (e.g. grid key 'fill.grid').
 * Data: array of { id, date, index }, max 10 entries; oldest removed when full.
 * When a puzzle has no empty cells, its entry can be removed.
 */

const STORAGE_PREFIX = "puzzle.";
const MAX_ENTRIES = 10;

/**
 * @param {string} key - e.g. 'fill.grid'
 * @returns {Array<{ id: string, date: string, index: number }>}
 */
export function getFillTabEntries(key) {
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * @param {string} key - e.g. 'fill.grid'
 * @param {Array<{ id: string, date: string, index: number }>} entries
 */
function setFillTabEntries(key, entries) {
    try {
        const list = Array.isArray(entries) ? entries.slice(0, MAX_ENTRIES) : [];
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(list));
    } catch (e) {
        console.warn("fillTabStorage setFillTabEntries failed", e);
    }
}

/**
 * Get stored tab index for a puzzle.
 * @param {string} key - e.g. 'fill.grid'
 * @param {string} puzzleId
 * @returns {number|undefined}
 */
export function getFillTabIndex(key, puzzleId) {
    const entries = getFillTabEntries(key);
    const entry = entries.find((e) => e.id === puzzleId);
    return entry != null ? entry.index : undefined;
}

/**
 * Save tab index for a puzzle. Adds or updates entry; keeps max 10, oldest removed.
 * @param {string} key - e.g. 'fill.grid'
 * @param {string} puzzleId
 * @param {number} index
 */
export function setFillTabIndex(key, puzzleId, index) {
    const entries = getFillTabEntries(key);
    const date = new Date().toISOString();
    const updated = entries.filter((e) => e.id !== puzzleId);
    updated.push({ id: puzzleId, date, index });
    updated.sort((a, b) => new Date(b.date) - new Date(a.date));
    setFillTabEntries(key, updated.slice(0, MAX_ENTRIES));
}

/**
 * Remove stored entry for a puzzle (e.g. when it has no empty cells).
 * @param {string} key - e.g. 'fill.grid'
 * @param {string} puzzleId
 */
export function removeFillTabEntry(key, puzzleId) {
    const entries = getFillTabEntries(key).filter((e) => e.id !== puzzleId);
    setFillTabEntries(key, entries);
}

/** Key for grid fill dialog */
export const FILL_TAB_KEY_GRID = "fill.grid";

/** Key for cross fill dialog */
export const FILL_TAB_KEY_CROSS = "fill.cross";
